import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import type { Bounty, User, UserSkill } from "@/lib/supabase/types"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import styles from "./discovery-page.module.css"

// ========== Data Types ==========

interface CategoryWithCount {
  name: string
  count: number
}

interface TopUser {
  user: User
  skills: UserSkill[]
}

type SectionStatus = "idle" | "loading" | "success" | "error"

interface SectionState<T> {
  data: T
  status: SectionStatus
  error: string | null
}

// ========== Skeleton Sub-components ==========

function GroupSkeleton() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={styles.groupCard}>
          <LoadingSkeleton variant="text" width={60} height={14} />
          <LoadingSkeleton variant="text" width={36} height={11} />
        </div>
      ))}
    </>
  )
}

function TaskSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={styles.taskCard}>
          <div className={styles.taskInfo}>
            <LoadingSkeleton variant="text" width="60%" height={15} />
            <LoadingSkeleton variant="text" width={60} height={15} />
          </div>
          <div className={styles.taskTags}>
            <LoadingSkeleton variant="text" width={40} height={22} borderRadius={12} />
            <LoadingSkeleton variant="text" width={55} height={22} borderRadius={12} />
            <LoadingSkeleton variant="text" width={48} height={22} borderRadius={12} />
          </div>
          <div className={styles.taskMeta}>
            <div className={styles.taskMetaLeft}>
              <LoadingSkeleton variant="text" width={30} height={18} borderRadius={4} />
              <LoadingSkeleton variant="text" width={70} height={14} />
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

function UserSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={styles.userCard}>
          <LoadingSkeleton variant="circular" width={42} height={42} />
          <div className={styles.userInfo}>
            <LoadingSkeleton variant="text" width="50%" height={14} />
            <LoadingSkeleton variant="text" width="35%" height={12} />
            <LoadingSkeleton variant="text" width="45%" height={12} />
          </div>
          <div className={styles.userTags}>
            <LoadingSkeleton variant="text" width={56} height={24} borderRadius={12} />
            <LoadingSkeleton variant="text" width={48} height={24} borderRadius={12} />
          </div>
        </div>
      ))}
    </>
  )
}

// ========== Error Retry Component ==========

interface ErrorRetryProps {
  message: string
  onRetry: () => void
}

function ErrorRetry({ message, onRetry }: ErrorRetryProps) {
  return (
    <div className={styles.errorState}>
      <p className={styles.errorText}>{message}</p>
      <button className={styles.retryBtn} onClick={onRetry} type="button">
        重试
      </button>
    </div>
  )
}

// ========== Data Fetching ==========

async function fetchCategories(): Promise<CategoryWithCount[]> {
  const supabase = getBrowserSupabaseClient()
  if (!supabase) return []

  const { data, error } = await supabase
    .from("bounties")
    .select("category")
    .not("category", "is", null)
    .neq("category", "")

  if (error) throw error
  if (!data) return []

  const countMap = new Map<string, number>()
  for (const row of data) {
    const cat = row.category as string
    countMap.set(cat, (countMap.get(cat) ?? 0) + 1)
  }

  return Array.from(countMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
}

async function fetchHotTasks(): Promise<Bounty[]> {
  const supabase = getBrowserSupabaseClient()
  if (!supabase) return []

  const { data, error } = await supabase
    .from("bounties")
    .select("*")
    .eq("status", "bidding")
    .order("reward_usdc", { ascending: false })
    .limit(6)

  if (error) throw error
  return data ?? []
}

async function fetchTopUsers(): Promise<TopUser[]> {
  const supabase = getBrowserSupabaseClient()
  if (!supabase) return []

  const { data: users, error: userError } = await supabase
    .from("users")
    .select("*")
    .gte("level", 3)
    .order("level", { ascending: false })
    .limit(5)

  if (userError) throw userError
  if (!users || users.length === 0) return []

  const userIds = users.map((u: User) => u.id)

  const { data: skills, error: skillError } = await supabase
    .from("user_skills")
    .select("*")
    .in("user_id", userIds)

  if (skillError) throw skillError

  const skillMap = new Map<string, UserSkill[]>()
  for (const skill of skills ?? []) {
    const list = skillMap.get(skill.user_id) ?? []
    list.push(skill)
    skillMap.set(skill.user_id, list)
  }

  return users.map((user: User) => ({
    user,
    skills: skillMap.get(user.id) ?? [],
  }))
}

// ========== Helper ==========

const LEVEL_LABELS: Record<number, string> = {
  1: "L1 新手",
  2: "L2 熟手",
  3: "L3 大师",
  4: "L4 专家",
  5: "L5 宗师",
}

function getLevelLabel(level: number): string {
  return LEVEL_LABELS[level] ?? `L${level}`
}

function getAvatarInitial(nickname: string): string {
  return nickname.charAt(0)
}

function formatReward(bbp: number): string {
  if (bbp >= 10000) {
    return `${(bbp / 10000).toFixed(1)}万`
  }
  return bbp.toLocaleString("zh-CN")
}

function formatDeadline(dateStr: string | null): string {
  if (!dateStr) return "已截止"
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return "已截止"
  if (diffDays <= 7) return `${diffDays}天后`
  return `${date.getMonth() + 1}/${date.getDate()}`
}

// ========== Main Component ==========

export default function DiscoveryPage() {
  const [searchQuery, setSearchQuery] = useState("")
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const [categories, setCategories] = useState<SectionState<CategoryWithCount[]>>({
    data: [],
    status: "idle",
    error: null,
  })

  const [tasks, setTasks] = useState<SectionState<Bounty[]>>({
    data: [],
    status: "idle",
    error: null,
  })

  const [topUsers, setTopUsers] = useState<SectionState<TopUser[]>>({
    data: [],
    status: "idle",
    error: null,
  })

  // --- Load categories ---
  const loadCategories = useCallback(async () => {
    setCategories((prev) => ({ ...prev, status: "loading", error: null }))
    try {
      const data = await fetchCategories()
      setCategories({ data, status: "success", error: null })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "加载分类失败"
      setCategories((prev) => ({ ...prev, status: "error", error: msg }))
    }
  }, [])

  // --- Load hot tasks ---
  const loadTasks = useCallback(async () => {
    setTasks((prev) => ({ ...prev, status: "loading", error: null }))
    try {
      const data = await fetchHotTasks()
      setTasks({ data, status: "success", error: null })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "加载任务失败"
      setTasks((prev) => ({ ...prev, status: "error", error: msg }))
    }
  }, [])

  // --- Load top users ---
  const loadTopUsers = useCallback(async () => {
    setTopUsers((prev) => ({ ...prev, status: "loading", error: null }))
    try {
      const data = await fetchTopUsers()
      setTopUsers({ data, status: "success", error: null })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "加载用户失败"
      setTopUsers((prev) => ({ ...prev, status: "error", error: msg }))
    }
  }, [])

  // Load all sections independently on mount
  useEffect(() => {
    loadCategories()
    loadTasks()
    loadTopUsers()
  }, [loadCategories, loadTasks, loadTopUsers])

  // Local search filter on tasks
  const filteredTasks = searchQuery.trim()
    ? tasks.data.filter((t) =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase().trim())
      )
    : tasks.data

  // Supabase unavailable fallback
  const supabaseAvailable = getBrowserSupabaseClient() !== null

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <h1>蓝血精英</h1>
        <p>发现 · <span>超级个体</span></p>
      </header>

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="搜索小组、帖子、任务…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Banner */}
      <div className={styles.banner}>
        <div className={styles.bannerItem}>平台公告</div>
      </div>

      {/* Categories Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>兴趣小组</h2>
          <Link to="/discovery/groups">更多</Link>
        </div>
        <div className={styles.groupList}>
          {!supabaseAvailable ? (
            <FallbackMessage message="数据库未连接" />
          ) : categories.status === "loading" ? (
            <GroupSkeleton />
          ) : categories.status === "error" ? (
            <ErrorRetry message={categories.error ?? "加载失败"} onRetry={loadCategories} />
          ) : categories.data.length === 0 ? (
            <EmptyMessage message="暂无分类" />
          ) : (
            categories.data.map((cat) => (
              <div
                key={cat.name}
                className={`${styles.groupCard} ${activeCategory === cat.name ? styles.active : ""}`}
                onClick={() =>
                  setActiveCategory((prev) => (prev === cat.name ? null : cat.name))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setActiveCategory((prev) => (prev === cat.name ? null : cat.name))
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span className={styles.groupName}>{cat.name}</span>
                <span className={styles.groupMeta}>{cat.count}个任务</span>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Hot Tasks Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>热门任务</h2>
          <Link to="/tasks">查看全部</Link>
        </div>
        <div className={styles.taskList}>
          {!supabaseAvailable ? (
            <FallbackMessage message="数据库未连接" />
          ) : tasks.status === "loading" ? (
            <TaskSkeleton />
          ) : tasks.status === "error" ? (
            <ErrorRetry message={tasks.error ?? "加载失败"} onRetry={loadTasks} />
          ) : filteredTasks.length === 0 ? (
            <EmptyMessage message={searchQuery.trim() ? "没有匹配的任务" : "暂无热门任务"} />
          ) : (
            filteredTasks.map((task) => (
              <Link key={task.id} to={`/tasks/${task.id}`} className={styles.taskCardLink}>
                <div className={styles.taskCard}>
                  <div className={styles.taskInfo}>
                    <h3>{task.title}</h3>
                    <p>{formatReward(task.reward_usdc)}</p>
                  </div>
                  {task.tech_tags && task.tech_tags.length > 0 && (
                    <div className={styles.taskTags}>
                      {task.tech_tags.map((tag) => (
                        <span key={tag} className={styles.taskTag}>{tag}</span>
                      ))}
                    </div>
                  )}
                    <div className={styles.taskMeta}>
                    <div className={styles.taskMetaLeft}>
                      <span className={styles.levelBadge}>L2+</span>
                      <span className={styles.taskSlots}>截止 {formatDeadline(task.bidding_deadline)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* Top Users Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>超级个体</h2>
          <Link to="/discovery/users">查看全部</Link>
        </div>
        <div className={styles.userList}>
          {!supabaseAvailable ? (
            <FallbackMessage message="数据库未连接" />
          ) : topUsers.status === "loading" ? (
            <UserSkeleton />
          ) : topUsers.status === "error" ? (
            <ErrorRetry message={topUsers.error ?? "加载失败"} onRetry={loadTopUsers} />
          ) : topUsers.data.length === 0 ? (
            <EmptyMessage message="暂无推荐用户" />
          ) : (
            topUsers.data.map(({ user, skills }) => (
              <Link key={user.id} to={`/discovery/users/${user.id}`} className={styles.userCardLink}>
                <div className={styles.userCard}>
                  <div className={styles.userAvatar}>
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.nickname} />
                    ) : (
                      getAvatarInitial(user.nickname)
                    )}
                  </div>
                  <div className={styles.userInfo}>
                    <div className={styles.userNameRow}>
                      <span className={styles.userName}>{user.nickname}</span>
                      {user.is_verified && (
                        <svg className={styles.verifiedBadge} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                        </svg>
                      )}
                    </div>
                    <span className={styles.userLevel}>{getLevelLabel(user.level)}</span>
                    <span className={styles.userSchool}>{user.school ?? user.company ?? user.direction ?? ""}</span>
                  </div>
                  <div className={styles.userTags}>
                    {skills.slice(0, 3).map((skill) => (
                      <span key={skill.id} className={styles.userTag}>{skill.skill_name}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>
    </div>
  )
}

// ========== Utility Sub-components ==========

function FallbackMessage({ message }: { message: string }) {
  return (
    <div className={styles.errorState}>
      <p className={styles.errorText}>{message}</p>
    </div>
  )
}

function EmptyMessage({ message }: { message: string }) {
  return (
    <div className={styles.errorState}>
      <p className={styles.errorText}>{message}</p>
    </div>
  )
}
